import {
  DatePipe,
  HashLocationStrategy,
  LocationStrategy,
} from "@angular/common";
import {
  HTTP_INTERCEPTORS,
  HttpClient,
  HttpClientModule,
  provideHttpClient,
} from "@angular/common/http";
import {
  APP_INITIALIZER,
  ApplicationConfig,
  importProvidersFrom,
  Injector,
} from "@angular/core";
import { provideAnimations } from "@angular/platform-browser/animations";
import {
  provideRouter,
  withInMemoryScrolling,
} from "@angular/router";
import {
  TranslateLoader,
  TranslateModule,
  TranslateService,
} from "@ngx-translate/core";
import { KeycloakAngularModule, KeycloakBearerInterceptor, KeycloakService } from "keycloak-angular";
import { v4 as uuidv4 } from "uuid";
import { environment } from "../environments/environment";
import { routes } from "./app.routes";
import { UserApiService } from "./services/generated/core-api/services/user-api.service";
import { RestHttpErrorsInterceptor } from "./shared/interceptors/rest-http-errors-interceptor";
import { HttpLoaderFactory } from "./shared/shared.module";
import { AngularFireModule } from "@angular/fire/compat";
import { AngularFireAuthModule } from "@angular/fire/compat/auth";
import { AngularFirestoreModule } from "@angular/fire/compat/firestore";
import { AngularFireMessagingModule } from "@angular/fire/compat/messaging";
import { AuthService } from "./services/auth/auth.service";

// Each generated folder has its own ApiConfiguration class = its own DI token.
// We alias them here to avoid name collisions and inject the correct URL per env.
import { ApiConfiguration as ErpApiConfig }     from "./services/generated/erp-api/api-configuration";
import { ApiConfiguration as CoreApiConfig }     from "./services/generated/core-api/api-configuration";
import { ApiConfiguration as WabaApiConfig }     from "./services/generated/waba-api/api-configuration";
import { ApiConfiguration as WabaBotApiConfig }  from "./services/generated/waba-bot-api/api-configuration";
import { ApiConfiguration as PaymentApiConfig }  from "./services/generated/payment-api/api-configuration";

export const appConfig: ApplicationConfig = {
  providers: [
    provideRouter(
      routes,
      withInMemoryScrolling({ scrollPositionRestoration: "top" })
    ),
    { provide: LocationStrategy, useClass: HashLocationStrategy },
    provideHttpClient(),
    provideAnimations(),
    DatePipe,
    importProvidersFrom(KeycloakAngularModule, HttpClientModule),
    {
      provide: APP_INITIALIZER,
      deps: [KeycloakService, Injector],
      useFactory: initializeKeycloak,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: KeycloakBearerInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: RestHttpErrorsInterceptor,
      multi: true,
    },
    importProvidersFrom(
      TranslateModule.forRoot({
        defaultLanguage: environment.defaultLang,
        loader: {
          provide: TranslateLoader,
          useFactory: HttpLoaderFactory,
          deps: [HttpClient],
        },
      })
    ),
    importProvidersFrom([
      AngularFireModule.initializeApp(environment.firebase),
      AngularFireAuthModule,
      AngularFirestoreModule,
      AngularFireMessagingModule,
    ]),

    // ─── API base URL injection ───────────────────────────────────────────
    // These providers override the hardcoded rootUrl in each generated
    // api-configuration.ts. Angular DI picks these up at runtime, so the
    // correct URL is used for whichever environment was built.
    // Angular's fileReplacements (angular.json) ensures the right
    // environment.ts is bundled at build time.
    // Use factories to return actual ApiConfiguration instances.
    // This avoids any edge-cases where code expects a class instance (not just a {rootUrl} object).
    {
      provide: ErpApiConfig,
      useFactory: () => Object.assign(new ErpApiConfig(), { rootUrl: environment.erpApiUrl }),
    },
    {
      provide: CoreApiConfig,
      useFactory: () => Object.assign(new CoreApiConfig(), { rootUrl: environment.coreApiUrl }),
    },
    {
      provide: WabaApiConfig,
      useFactory: () => Object.assign(new WabaApiConfig(), { rootUrl: environment.wabaApiUrl }),
    },
    {
      provide: WabaBotApiConfig,
      useFactory: () => Object.assign(new WabaBotApiConfig(), { rootUrl: environment.wabaBotApiUrl }),
    },
    {
      provide: PaymentApiConfig,
      useFactory: () => Object.assign(new PaymentApiConfig(), { rootUrl: environment.paymentApiUrl }),
    },
    // ─────────────────────────────────────────────────────────────────────

    //TranslateService,
  ],
};

function initializeKeycloak(keycloak: KeycloakService, injector: Injector) {
  return () =>
    keycloak
      .init({
        config: {
          url: environment.keycloak.url,
          realm: environment.keycloak.realm,
          clientId: environment.keycloak.clientId,
        },
        enableBearerInterceptor: true,
        initOptions: {
          onLoad: "check-sso",
          silentCheckSsoRedirectUri:
            window.location.origin + "/assets/silent-check-sso.html",
            redirectUri: window.location.href,
        },
      })
      .then(async () => {
        const token = await keycloak.getToken();
        if (token) {
          const decoded: any = JSON.parse(atob(token!.split(".")[1]));
          const now = Math.floor(Date.now() / 1000);
          const authTime = decoded.auth_time;
          if (now - authTime < 5) {
            const keycloakId =
              keycloak.getKeycloakInstance().idTokenParsed?.sub || "";
            const userService = injector.get(UserApiService);
            userService
              .updateLastLogin({
                "x-api-canal": "web",
                "x-api-requestId": uuidv4(),
                body: keycloakId,
              })
              .subscribe({
                next: () => {},
                error: () => console.error("Error updating last login"),
              });
          }
        }
      });
}
